import { Dialog } from "@headlessui/react";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { FormBox, ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Controller, useForm } from "react-hook-form";
import { SelectFields } from "../../components/select/SelectFields";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import FormController from "../../components/fields/FormController";
import { PtCategorySettings, PointCategory, pointCategorySchema, newPtCategorySettings } from "../../model/PointData";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { useInput } from "../../hooks/useInput";
import { yupResolver } from "@hookform/resolvers/yup";
import { convertMonthToString } from "../../utility/ConvertUtily";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

export type PointCategoryEditProps = {
  settings?: PtCategorySettings;
  onClickSubmit: (data: PtCategorySettings & {expirationYmString: string}) => Promise<void>;
  pointCategorySelected?: PointCategory; // 選択状態のポイントカテゴリ
  setPointCategorySelectedToBase: (id: number) => void;
  pointCategoryList: PointCategory[]; // ポイントカテゴリリスト
  ptCategorySettingsList:PtCategorySettings[]; // ポイントカテゴリ設定リスト
  setPointCategoryList?: React.Dispatch<React.SetStateAction<PointCategory[]>>; //ポイントカテゴリの書き換えステート
  ptCategorySettingInitial: PtCategorySettings; // ポイントカテゴリ設定の初期表示データ(先頭のポイントカテゴリに紐づくデータ)
}

export const PointCategoryEditModal = ({
  open, toggle, closeOnClickOutside,
  settings,
  onClickSubmit,
  pointCategoryList,
  ptCategorySettingsList,
  ptCategorySettingInitial,
  setPointCategorySelectedToBase,
}: ModalProp & PointCategoryEditProps) => {

  const [pointCategoryItems, setPointCategoryItems] = useState([]);
  const [pointCategorySelected, setPointCategorySelected] = useState(0);
  const [ptCategorySetting, setPtCategorySetting] = useState<PtCategorySettings>();
  const limitPointFlg = useInput(ptCategorySettingInitial.limitPointFlg);
  const [unitSelected, setUnitSelected] = useState<string | undefined>(ptCategorySettingInitial.numberOfGrantsMap ? ptCategorySettingInitial.numberOfGrantsMap.unit : '');
  const expireFlg = useInput(ptCategorySettingInitial.expireFlg);
  const [expirationUnitSelected, setExpirationUnitSelected] = useState<string | undefined>(ptCategorySettingInitial.dateOfExpiryMap ? ptCategorySettingInitial.dateOfExpiryMap.expirationUnit : '');
  const [expirationValueSelected, setExpirationValueSelected] = useState<number | undefined | null>(ptCategorySettingInitial.dateOfExpiryMap ? ptCategorySettingInitial.dateOfExpiryMap.expirationValue : undefined);
  const [baseMonthSelected, setBaseMonthSelected] = useState<number | undefined | null>(ptCategorySettingInitial.numberOfGrantsMap ? ptCategorySettingInitial.numberOfGrantsMap.baseMonth : undefined);
  const [isUnitEnabled, setIsUnitEnabled] = useState(limitPointFlg.value === "limitPointFlgFalse");
  const [isBaseMonthEnabled, setIsBaseMonthEnabled] = useState(limitPointFlg.value === "limitPointFlgFalse");
  const [isLimitPointEnabled, setIsLimitPointEnabled] = useState(limitPointFlg.value === "limitPointFlgFalse");
  const [isExpirationUnitEnabled, setExpirationUnitEnabled] = useState(expireFlg.value === "expireFlgFalse");
  const [isExpirationValueEnabled, setExpirationValueEnabled] = useState(expireFlg.value === "expireFlgFalse");
  const [isExpirationYmEnabled, setExpirationYmEnabled] = useState(expireFlg.value === "expireFlgFalse");
  const [isInitialDisplay, setIsInitialDisplay] = useState(true);
  const [limitPoint, setLimitPoint] = useState<number | string | undefined>(ptCategorySettingInitial.numberOfGrantsMap ? ptCategorySettingInitial.numberOfGrantsMap.limitPoint : '');
  const [expirationValue, setExpirationValue] = useState<number | string | undefined>(ptCategorySettingInitial.dateOfExpiryMap ? ptCategorySettingInitial.dateOfExpiryMap.expirationValue : '')
  const [expirationYmString, setExpirationYmString] = useState<string>("");
  const [contentFlg, setContentFlg] = useState(true);
  const [ptCategorySettings, setPtCategorySettings] = useState<PtCategorySettings[]>([]);
  
  const resolver = useMemo(() => {
    return yupResolver(pointCategorySchema(
      limitPointFlg.value === "limitPointFlgTrue",
      expireFlg.value === "expireFlgTrue"
    ));
  }, [limitPointFlg.value, expireFlg.value]);

  const { control, handleSubmit, setValue, clearErrors, formState: {errors}, trigger} = useForm<PtCategorySettings & {expirationYmString: string}>({
    resolver: resolver,
    defaultValues: settings,
    mode: 'all',
  });

  useEffect(() => {
    // ポイントカテゴリのリスト取得
    let pointCategoryItems:any = [];
    pointCategoryList.map((item) => {
      pointCategoryItems.push({id:item.categoryId, name:item.categoryName});
    });
    setPointCategoryItems(pointCategoryItems);
    setIsInitialDisplay(false);
  }, []);

  useEffect(() => {
    const ptCategorySettings = [];
    for(let i = 0; i < 3; i++){
      const newPtCategorySetting = Object.assign({}, newPtCategorySettings);
      newPtCategorySetting.categoryId = i;
      newPtCategorySetting.categoryName = pointCategoryList.filter((category) => category.categoryId === i)[0].categoryName;
      const result = ptCategorySettingsList.filter((category) => category.categoryId === i)[0] ?? newPtCategorySetting;
      ptCategorySettings.push(result);
    }
    setPtCategorySettings(ptCategorySettings);
    const ptCategorySettingData = ptCategorySettingsList.filter((category) => category.categoryId === pointCategorySelected);
    const ptCategorySetting = ptCategorySettingData.length > 0 ? ptCategorySettingData[0] : newPtCategorySettings;
    setPtCategorySetting(ptCategorySetting);
    setValue("id", ptCategorySetting.id ?? '');
    setValue("tenantId", ptCategorySetting.tenantId ?? '');
    setValue("categoryId", pointCategorySelected ?? '');
    setValue("categoryName", pointCategoryList.filter((list) => list.categoryId === pointCategorySelected)[0].categoryName ?? '');
    setValue("limitPointFlg", ptCategorySetting.limitPointFlg ?? false)
    setValue("numberOfGrantsMap", ptCategorySetting.numberOfGrantsMap ?? {});
    setValue("numberOfGrantsMap.unit", ptCategorySetting.numberOfGrantsMap?.unit ?? "");
    setValue("numberOfGrantsMap.baseMonth", ptCategorySetting.numberOfGrantsMap?.baseMonth ?? 0);
    setValue("numberOfGrantsMap.limitPoint", ptCategorySetting.numberOfGrantsMap?.limitPoint);
    setValue("expireFlg", ptCategorySetting.expireFlg ?? false);
    setValue("dateOfExpiryMap", ptCategorySetting.dateOfExpiryMap ?? {});
    setValue("dateOfExpiryMap.expirationUnit", ptCategorySetting.dateOfExpiryMap?.expirationUnit ?? "");
    setValue("dateOfExpiryMap.expirationValue", ptCategorySetting.dateOfExpiryMap?.expirationValue);
    limitPointFlg.setValue(ptCategorySetting.limitPointFlg ? "limitPointFlgTrue" : "limitPointFlgFalse");
    setUnitSelected(ptCategorySetting.numberOfGrantsMap?.unit);
    expireFlg.setValue(ptCategorySetting.expireFlg ? "expireFlgTrue" : "expireFlgFalse");
    setExpirationUnitSelected(ptCategorySetting.dateOfExpiryMap?.expirationUnit);
    setBaseMonthSelected(ptCategorySetting.numberOfGrantsMap?.baseMonth);
    setExpirationValueSelected(ptCategorySetting.dateOfExpiryMap?.expirationValue);
    const expirationYm = ptCategorySetting.dateOfExpiryMap?.expirationYm;
    if (expirationYm && typeof expirationYm === "object") {
      let dateObj: Date;
      if ("toDate" in expirationYm && typeof expirationYm.toDate === "function") {
        dateObj = expirationYm.toDate();
      } else {
        dateObj = new Date(expirationYm);
      }
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = ("00" + (dateObj.getMonth() + 1)).slice(-2);
        const ym = `${year}-${month}`;
        setValue("expirationYmString", ym);
        setExpirationYmString(ym);
      } else {
        setValue("expirationYmString", "");
      }
    }
    setLimitPoint(ptCategorySetting.numberOfGrantsMap?.limitPoint ?? '');
    setExpirationValue(ptCategorySetting.dateOfExpiryMap?.expirationValue ?? '');
  },[setValue, ptCategorySettingsList, pointCategorySelected]);

  // ポイント数合計上限の変更ハンドラー
  useEffect(() => {
    setValue("limitPointFlg", (limitPointFlg.value === "limitPointFlgTrue"));
    if (limitPointFlg.value === "limitPointFlgTrue") {
      setIsUnitEnabled(true);
      setIsLimitPointEnabled(true);
      if (unitSelected === "year") {
        setIsBaseMonthEnabled(true);
      } else {
        setIsBaseMonthEnabled(false);
        setBaseMonthSelected(0);
      }
    } else {
      setIsUnitEnabled(false);
      setIsBaseMonthEnabled(false);
      setIsLimitPointEnabled(false);
      if (isInitialDisplay === false) {
        setUnitSelected("");
        setBaseMonthSelected(0);
        setLimitPoint("");
        setValue("numberOfGrantsMap.unit", "");
        setValue("numberOfGrantsMap.baseMonth", undefined);
        setValue("numberOfGrantsMap.limitPoint", undefined);
        
        trigger("numberOfGrantsMap");
      }
    }
  }, [limitPointFlg.value, unitSelected, setValue]);

  // ポイント有効期限の変更ハンドラー
  useEffect(() => {
    setValue("expireFlg", (expireFlg.value === "expireFlgTrue"));

    if (expireFlg.value === "expireFlgTrue") {
      setExpirationUnitEnabled(true);
      setExpirationValueEnabled(true);
      setExpirationYmEnabled(true);
    } else {
      setExpirationUnitEnabled(false);
      setExpirationValueEnabled(false);
      setExpirationYmEnabled(false);

      if (!isInitialDisplay) {
        setExpirationUnitSelected("");
        setExpirationValue('');
        setExpirationValueSelected(null);
        setExpirationYmString("");
        setValue("dateOfExpiryMap.expirationUnit", "");
        setValue("dateOfExpiryMap.expirationValue", undefined);
        setValue("dateOfExpiryMap.expirationYm", undefined);
        setValue("expirationYmString", "");

        trigger("dateOfExpiryMap");
      }
    }
  }, [expireFlg.value, setValue]);

  useEffect(() => {
    setValue("numberOfGrantsMap.limitPoint", limitPoint ? Number(limitPoint) : undefined);
    setValue("dateOfExpiryMap.expirationValue", expirationValue ? Number(expirationValue) : undefined);
  }, [limitPoint, expirationValue]);

  useEffect(() => {
    if (isBaseMonthEnabled === false) {
      trigger("numberOfGrantsMap.baseMonth");
    }
  }, [isBaseMonthEnabled])

  const limitPointFlgItems: RadioButtonItem[] = [
    {id: "limitPointFlgFalse", title: "設定なし"},
    {id: "limitPointFlgTrue", title: "設定あり"}
  ];

  const unitItems = [
    {value: "", name: ""},
    {value: "none", name: "期限なし"},
    {value: "year", name: "１年間"},
    {value: "month", name: "１ヵ月"},
  ];

  const baseMonthItems = [
    {value: null, name: ""},
    {value: 1, name: "1"},
    {value: 2, name: "2"},
    {value: 3, name: "3"},
    {value: 4, name: "4"},
    {value: 5, name: "5"},
    {value: 6, name: "6"},
    {value: 7, name: "7"},
    {value: 8, name: "8"},
    {value: 9, name: "9"},
    {value: 10, name: "10"},
    {value: 11, name: "11"},
    {value: 12, name: "12"},
  ];

  const expireFlgItems: RadioButtonItem[] = [
    {id: "expireFlgFalse", title: "設定なし"},
    {id: "expireFlgTrue", title: "設定あり"}
  ];

  const expirationUnitItems = [
    {value: "", name: ""},
    {value: "year", name: "期間(年)"},
    {value: "month", name: "期間(月)"},
    {value: "limitMonth", name: "期限年月"},
  ];

  const expirationValueItems = [
    {value: null, name: ""},
    {value: 1, name: "1"},
    {value: 2, name: "2"},
    {value: 3, name: "3"},
    {value: 4, name: "4"},
    {value: 5, name: "5"},
    {value: 6, name: "6"},
    {value: 7, name: "7"},
    {value: 8, name: "8"},
    {value: 9, name: "9"},
    {value: 10, name: "10"},
    {value: 11, name: "11"},
    {value: 12, name: "12"},
  ];

  const onUnitSelect = (value: string) => {
    setUnitSelected(value);
    setBaseMonthSelected(null);
    setValue("numberOfGrantsMap.baseMonth", 0);
  };

  const onBaseMonthSelect = (value: number) => {
    setBaseMonthSelected(value);
  };

  const onExpirationUnitSelect = (value: string) => {
    setExpirationUnitSelected(value);
    setExpirationValueSelected(null);
    setExpirationYmString("");
    setExpirationValue('');
    setValue("expirationYmString", "");
    setValue("dateOfExpiryMap.expirationValue", undefined);
    setValue("dateOfExpiryMap.expirationYm", undefined);
  
    clearErrors("dateOfExpiryMap");
    clearErrors("expirationYmString");
  };

  const onExpirationValueSelect = (value: number) => {
    setExpirationValueSelected(value);
  };

  const convertExpirationYm = (date: any) => {
    if(date.seconds){
      date = new Date(date.seconds * 1000);
    }
    return convertMonthToString(date);
  }

  const upIcon = {icon: ChevronUpIcon};
  const downIcon = {icon: ChevronDownIcon};

  // カテゴリ詳細画面の表示・非表示の切り替え
  const handleClick = (e:any) => {
    e.preventDefault();
    setContentFlg(contentFlg? false : true);
  };

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <div className="overflow-x-auto">
          <Dialog.Panel className="transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-[calc(100vw_-_10rem)] min-w-[670px] max-w-[710px]">
            <div className="px-2 pb-4 sm:p-4">
              <div className="mt-3">
                <Dialog.Title
                  as="h3"
                  className="mb-2 text-left text-lg font-medium leading-6 text-gray-900"
                >
                  カテゴリ設定
                </Dialog.Title>
                <div className="overflow-y-auto">
                  <div className="px-2 text-left pb-8 max-h-[calc(100vh_-_14rem)] ">
                    <div className="mb-4 mt-1 p-1 relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-md bg-indigo-50">
                      <a className="flex justify-between items-center mx-1 cursor-pointer" onClick={(e) => handleClick(e)}>
                        <h2 className=" flex text-sm items-center py-1">
                          現在の設定内容
                        </h2>
                        {contentFlg?(
                          <>
                            <upIcon.icon
                              className={"flex-shrink-0 h-5 w-5"}
                              aria-hidden="true"
                            />
                          </>
                        ):(
                          <>
                            <downIcon.icon
                              className={"flex-shrink-0 h-5 w-5"}
                              aria-hidden="true"
                            />
                          </>
                        )}
                      </a>
                      {contentFlg?(
                        <>
                          <div className="bg-indigo-50 mx-2 mb-2 py-1 px-2 h-auto">
                            {ptCategorySettings.map((item) => {
                              return(
                                <>
                                  <div className="my-3">
                                    <div className="text-sm ml-2">{item.categoryName}</div>
                                    <div className={`ml-6 text-sm text-gray-700 whitespace-nowrap`}>
                                      ポイント数合計上限
                                      <div className="ml-6">
                                        {item?.limitPointFlg ? "設定あり" : item?.id !== "" ? "設定なし" : "未登録"}
                                        {item?.limitPointFlg ? 
                                          <>
                                          （ポイント獲得期間：
                                            {item?.numberOfGrantsMap?.unit === "none" ? "期限なし" : 
                                              item?.numberOfGrantsMap?.unit === "year" ? "1年間" : "1ヵ月"}
                                            　基準月：
                                            {item?.numberOfGrantsMap?.baseMonth ?? ""}
                                            　ポイント上限：
                                            {item?.numberOfGrantsMap?.limitPoint ?? ""}）
                                          </>: <></>
                                        }
                                      </div>
                                    </div>
                                    <div className={`ml-6 text-sm text-gray-700 whitespace-nowrap`}>
                                      ポイント有効期限
                                      <div className="ml-6">
                                        {item?.expireFlg ? "設定あり" : item?.id !== "" ? "設定なし" : "未登録"}
                                        {item?.expireFlg ? 
                                          <>
                                          （指定方法：
                                            {item?.dateOfExpiryMap?.expirationUnit === "year" ? "期間(年)" : 
                                              item?.dateOfExpiryMap?.expirationUnit === "month" ? "期間(月)" : "期限年月"}
                                            　設定値：
                                            {item?.dateOfExpiryMap?.expirationValue ? item?.dateOfExpiryMap?.expirationValue : 
                                              item?.dateOfExpiryMap?.expirationYm ? convertExpirationYm(item?.dateOfExpiryMap?.expirationYm) : ""}）
                                          </>: <></>
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </>
                              );
                            })}
                            
                          </div>
                        </>):(<></>)
                      }
                    </div>

                    <FormBox gridcol="max-w-[13rem] sm:pt-[0px]">
                      <Controller
                        name={"categoryId"}
                        control={control}
                        render={({ field, fieldState }) => (
                          <SelectFields
                            {...field}
                            label="ポイントカテゴリ"
                            color="indigo"
                            className="h-[4.5rem]"
                            selectClassName="px-3 py-2 h-auto"
                            onChange={(e: any) => {setPointCategorySelected(Number(e.target.value)); setPointCategorySelectedToBase(Number(e.target.value))}}
                            value={pointCategorySelected}
                            selectItems={pointCategoryItems}
                            error={fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </FormBox>

                    <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5">
                      <FormBox gridcol="sm:grid-cols-1 sm:pt-[0px] flex flex-col">
                        <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>ポイント数合計上限</span>
                        <div className="pl-5 pt-2 pb-5">
                          <Controller
                            name={"limitPointFlg"}
                            control={control}
                            render={({ field, fieldState }) => (
                              <RadioButton
                                children={undefined}
                                name={"limitPointFlg"}
                                value={limitPointFlg}
                                radioButtonItem={limitPointFlgItems}
                              />
                            )}
                          />
                          <div className="pl-7 sm:pr-7">
                            <div className="flex text-left sm:items-start gap-6 h-20">
                              <Controller
                                name={"numberOfGrantsMap.unit"}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <div className="flex flex-col gap-y-2">
                                    <SelectFields
                                      {...field}
                                      label="ポイント獲得期間"
                                      color="indigo"
                                      className="flex items-center gap-x-3 pt-5 w-[280px]"
                                      selectClassName={`px-3 py-2 h-auto ${!isUnitEnabled ? 'bg-gray-400' : ''}`}
                                      onChange={(e: any) => {onUnitSelect(e.target.value);field.onChange(e)}}
                                      value={unitSelected}
                                      selectItems={unitItems}
                                      error={fieldState.error}
                                      style={{'width': '8rem'}}
                                      disabled={!isUnitEnabled}
                                      required
                                    />
                                    {fieldState.error?.message && (
                                      <span className="block text-xs text-red-500" style={{marginLeft: '140px', maxWidth: '100%'}}>{fieldState.error.message}</span>
                                    )}
                                  </div>
                                )}
                              />
                              <Controller
                                name={"numberOfGrantsMap.baseMonth"}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <div className="flex flex-col gap-y-2">
                                    <SelectFields
                                      {...field}
                                      label="基準月"
                                      color="indigo"
                                      className="flex items-center gap-x-3 pt-5 pl-[10px] w-fit"
                                      selectClassName={`px-3 py-2 h-auto ${!isBaseMonthEnabled ? 'bg-gray-400' : ''}`}
                                      onChange={(e: any) => {onBaseMonthSelect(Number(e.target.value));field.onChange(Number(e.target.value))}}
                                      value={baseMonthSelected}
                                      selectItems={baseMonthItems?.map((item) => {
                                        return {
                                          value: item.value,
                                          name: item.name,
                                        }
                                      })}
                                      error={fieldState.error}
                                      style={{'width': '5rem'}}
                                      disabled={!isBaseMonthEnabled}
                                      required
                                    />
                                    {fieldState.error?.message && (
                                      <div className="block text-xs text-red-500" style={{marginLeft: '69px'}}>{fieldState.error.message}</div>
                                    )}
                                  </div>
                                )}
                              />
                            </div>
                            <FormController
                              control={control}
                              name={"numberOfGrantsMap.limitPoint"}
                              label={"ポイント上限"}
                              type={"text"}
                              style={{'width': '8rem', 'text-align': 'right'}}
                              className={"pl-7 pt-5 flex items-center gap-x-3"}
                              inputClassName={`${!isLimitPointEnabled ? 'bg-gray-400 opacity-70' : ''}`}
                              maxLength={6}
                              disabled={!isLimitPointEnabled}
                              value={limitPoint}
                              onChange={(e: { target: { value: SetStateAction<string | number | undefined>; }; }) => setLimitPoint(e.target.value)}
                              required
                            />
                          </div>
                        </div>
                      </FormBox>
                    </div>

                    <div className={"pb-2"}>
                      <div className="relative mt-8 shadow ring-1 ring-black ring-opacity-5">
                        <FormBox gridcol="sm:grid-cols-1 sm:pt-[0px]">
                          <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>ポイント有効期限</span>
                          <div className="pl-5 pt-2 pb-5">
                            <Controller
                              name={"expireFlg"}
                              control={control}
                              render={({ field, fieldState }) => (
                                <RadioButton
                                  children={undefined}
                                  name={"expireFlg"}
                                  value={expireFlg}
                                  radioButtonItem={expireFlgItems}
                                />
                              )}
                            />
                            <div className="pl-20">
                              <Controller
                                name={"dateOfExpiryMap.expirationUnit"}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <SelectFields
                                    {...field}
                                    label="指定方法"
                                    color="indigo"
                                    className="flex items-center gap-x-3 pl-1 pt-5"
                                    selectClassName={`px-3 py-2 h-auto ${!isExpirationUnitEnabled ? 'bg-gray-400' : ''}`}
                                    onChange={(e: any) => {onExpirationUnitSelect(e.target.value);field.onChange(e);}}
                                    value={expirationUnitSelected}
                                    selectItems={expirationUnitItems}
                                    error={fieldState.error}
                                    helperText={fieldState.error?.message}
                                    style={{'width': '8rem'}}
                                    disabled={!isExpirationUnitEnabled}
                                    required
                                  />
                                )}
                              />
                              {expirationUnitSelected === "month" ? 
                                <Controller
                                  control={control}
                                  name={"dateOfExpiryMap.expirationValue"}
                                  render={({ field, fieldState }) => (
                                    <SelectFields
                                      {...field}
                                      label="設定値"
                                      color="indigo"
                                      className="flex items-center gap-x-3 pl-[17px] pt-5"
                                      selectClassName={`px-3 py-2 h-auto ${!isExpirationValueEnabled ? 'bg-gray-400' : ''}`}
                                      onChange={(e: any) => {onExpirationValueSelect(Number(e.target.value));field.onChange(Number(e.target.value))}}
                                      value={expirationValueSelected}
                                      selectItems={expirationValueItems?.map((item) => {
                                        return {
                                          value: item.value,
                                          name: item.name,
                                        }
                                      })}
                                      error={fieldState.error}
                                      helperText={fieldState.error?.message}
                                      style={{'width': '5rem'}}
                                      disabled={!isExpirationValueEnabled}
                                      required
                                    />
                                  )}
                                />
                              : expirationUnitSelected === "limitMonth" ?
                                <FormController
                                  control={control}
                                  name={"expirationYmString"}
                                  label={"設定値"}
                                  type={"month"}
                                  style={{'width': '8rem'}}
                                  className={"flex items-center gap-x-3 pl-[17px] pt-5"}
                                  inputClassName={`${!isExpirationYmEnabled ? 'bg-gray-400 opacity-70' : ''}`}
                                  disabled={!isExpirationYmEnabled}
                                  required
                                  value={expirationYmString || ""}
                                  onChange={(e: { target: { value: any; }; }) => {
                                    const newVal = e.target.value;
                                    setExpirationYmString(newVal);
                                    setValue("expirationYmString", newVal, { shouldValidate: true });
                                  }}
                                />
                              :
                                <FormController
                                  control={control}
                                  name={"dateOfExpiryMap.expirationValue"}
                                  label={"設定値"}
                                  type={"text"}
                                  style={{'width': '8rem', 'text-align': 'right'}}
                                  className={"flex items-center gap-x-3 pl-[17px] pt-5"}
                                  inputClassName={`${!isExpirationValueEnabled ? 'bg-gray-400 opacity-70' : ''}`}
                                  maxLength={2}
                                  disabled={!isExpirationValueEnabled}
                                  value={expirationValue}
                                  onChange={(e: { target: { value: SetStateAction<string | number | undefined>; }; }) => setExpirationValue(e.target.value)}
                                  required
                                />
                              }
                            </div>
                          </div>
                        </FormBox>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ModalFoterBox>
              <Button
                className="mx-2 mt-1 w-[100px]"
                outline
                onClick={toggle}
                children={"閉じる"}
              />
              <Button
                className="mt-1 w-[100px]"
                onClick={handleSubmit(onClickSubmit)}
                children={"登録"}
              />
            </ModalFoterBox>
          </Dialog.Panel>
        </div>
      </Modal>
    </>
  );
};